import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase-config';
import { collection, addDoc, query, where, getDocs, orderBy, serverTimestamp } from 'firebase/firestore';

export default function PatientManager({ currentUser, onSelectPatient, selectedPatientId }) {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [dob, setDob] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (currentUser) fetchPatients();
        else setPatients([]);
    }, [currentUser]);

    const fetchPatients = async () => {
        setLoading(true);
        try {
            const patientsRef = collection(db, 'patients');
            // Avoid composite index requirement by filtering only and sorting client-side
            const q = query(patientsRef, where('ownerId', '==', currentUser.uid));
            const snap = await getDocs(q);
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Normalize createdAt to milliseconds for robust client-side sorting
            const toMillis = (v) => {
                if (!v) return 0;
                if (typeof v === 'number') return v;
                if (v.seconds) return v.seconds * 1000;
                if (v.toDate) return v.toDate().getTime();
                return 0;
            };

            list.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

            setPatients(list);
        } catch (err) {
            console.error('Error fetching patients:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!currentUser) return alert('Please sign in to create patients');
        if (!firstName.trim() || !lastName.trim() || !dob) return alert('Please fill first name, last name, and DOB');
        try {
            const patientsRef = collection(db, 'patients');
            const docRef = await addDoc(patientsRef, {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                dob,
                ownerId: currentUser.uid,
                createdAt: serverTimestamp(),
            });
            // Refresh list and select the newly created patient
            await fetchPatients();
            onSelectPatient(docRef.id);
            setFirstName(''); setLastName(''); setDob('');
        } catch (err) {
            console.error('Error creating patient:', err);
            alert('Failed to create patient. Check console for details.');
        }
    };

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredPatients = normalizedSearch
        ? patients.filter((p) => {
            const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim().toLowerCase();
            const dobValue = `${p.dob || ''}`.toLowerCase();
            return fullName.includes(normalizedSearch) || dobValue.includes(normalizedSearch);
        })
        : patients;

    return (
        <div style={{ border: '1px solid #eee', padding: '0.75em', borderRadius: 6, marginBottom: '1em' }}>
            <h4 style={{ marginTop: 0 }}>Patients</h4>
            <form onSubmit={handleCreate} style={{ display: 'flex', gap: '0.5em', alignItems: 'center', marginBottom: '0.5em' }}>
                <input placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <input placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                <input type="date" placeholder="DOB" value={dob} onChange={(e) => setDob(e.target.value)} />
                <button type="submit">Create Patient</button>
            </form>

            {loading ? <div>Loading patients...</div> : (
                <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                    <input
                        placeholder="Search patients"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: '100%', marginBottom: '0.5em' }}
                    />
                    {filteredPatients.length === 0 && <p style={{ margin: 0 }}>No patients match</p>}
                    {filteredPatients.map(p => (
                        <div key={p.id} onClick={() => onSelectPatient(p.id)} style={{ padding: '0.5em', marginBottom: '0.4em', background: selectedPatientId === p.id ? '#e3f2fd' : '#f8f8f8', borderRadius: 4, cursor: 'pointer' }}>
                            <strong>{p.firstName} {p.lastName}</strong>
                            <div style={{ fontSize: '0.85em', color: '#666' }}>{p.dob}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
